import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { Redirect } from 'react-router-dom';
import TopBar from "../components/TopBar";
import Sidebar from "../components/Sidebar";
import { getLevels, editImpact, editProbability } from "../store/actions/riskActions";
import ContentEditable from 'react-contenteditable';

function RiskLevels(props) {
    const { token, levels, getLevelsFN, editImpactFN, editProbabilityFN } = props;

    const colorsProbabilities = [
        { value: 'Rare', label: 'Rare', color: "#0081A7" },
        { value: 'Unlikely', label: 'Unlikely', color: "#00AFB9" },
        { value: 'Possible', label: 'Possible', color: "#FED9B7" },
        { value: 'Likely', label: 'Likely', color: "#F07167" },
        { value: 'Almost certain', label: 'Almost Certain', color: "#b4170b" }
    ];

    const colorsImpacts = [
        { value: 'Insignificant', label: 'Insignificant', color: "#0081A7" },
        { value: 'Minor', label: 'Minor', color: "#00AFB9" },
        { value: 'Moderate', label: 'Moderate', color: "#FED9B7" },
        { value: 'Major', label: 'Major', color: "#F07167" },
        { value: 'Catastrophe', label: 'Catastrophe', color: "#b4170b" }
    ];

    const editableProbability = useRef([]);
    const editableImpact = useRef([]);

    useEffect(() => {
        getLevelsFN();
    }, [getLevelsFN]);

    useEffect(() => {
        if (levels) {
            editableProbability.current = levels.riskProbabilities?.map(probability => ({
                controls: probability.controls,
                frequency: probability.frequency,
            })) || [];
            editableImpact.current = levels.riskImpacts?.map(impact => ({ ...impact })) || [];
        }
    }, [levels]);

    const handleInputChange = (value) => {
        const parsedValue = parseInt(value, 10);
        return isNaN(parsedValue) ? '' : parsedValue;
    };

  return token ? (
    <>
      <div className="flex-none w-full px-12 py-10 overflow-auto">
        {/* Page Title */}
        <div className="mb-6">
          <h1 className="text-lg font-semibold text-gray-900">Risk Assessment</h1>
        <p className="text-sm text-gray-500">Define and manage risk probability and impact levels</p>
      </div>

      {/* ===== Probability Levels ===== */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm mb-12">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-[15px] font-semibold text-gray-900">Probability Levels</h2>
          <p className="text-sm text-gray-500">Define probability ratings for risk assessment</p>
        </div>

        {levels?.riskProbabilities?.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-gray-800">
              <thead className="bg-white">
                <tr className="border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-gray-700 font-medium">Level</th>
                  <th className="px-6 py-3 text-left text-gray-700 font-medium">Score</th>
                  <th className="px-6 py-3 text-left text-gray-700 font-medium">Percentage</th>
                  <th className="px-6 py-3 text-left text-gray-700 font-medium">Description</th>
                </tr>
              </thead>

              <tbody>
                {levels.riskProbabilities.map((probability, index) => {
                  const color =
                    colorsProbabilities.find(c => c.value === probability.scale)?.color || '#e5e7eb';
                  return (
                    <tr key={index} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-6 py-3 text-gray-900">{probability.scale}</td>

                      <td className="px-6 py-3">
                        <span
                          className="inline-flex items-center justify-center w-6 h-6 rounded-full text-[12px] font-semibold"
                          style={{ backgroundColor: `${color}22`, color }}
                        >
                          {index + 1}
                        </span>
                      </td>

                      <td className="px-6 py-3 text-gray-700">
                        <ContentEditable
                          html={editableProbability.current[index]?.frequency}
                          disabled={false}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                          }}
                          onChange={(e) => { editableProbability.current[index].frequency = e.target.value; }}
                          onBlur={() => {
                            editProbabilityFN(
                              probability.id,
                              editableProbability.current[index].controls,
                              editableProbability.current[index].frequency
                            );
                          }}
                          tagName="span"
                        />
                      </td>

                      <td className="px-6 py-3 text-gray-700">
                        <ContentEditable
                          html={editableProbability.current[index]?.controls}
                          disabled={false}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                          }}
                          onChange={(e) => { editableProbability.current[index].controls = e.target.value; }}
                          onBlur={() => {
                            editProbabilityFN(
                              probability.id,
                              editableProbability.current[index].controls,
                              editableProbability.current[index].frequency
                            );
                          }}
                          tagName="span"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="px-6 py-3 text-sm text-gray-500 border-t border-gray-100">
              {levels.riskProbabilities.length} probability levels defined
            </div>
          </div>
        )}
      </div>

      {/* ===== Impact Levels ===== */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-[15px] font-semibold text-gray-900">Impact Levels</h2>
          <p className="text-sm text-gray-500">Define impact ratings for your organization</p>
        </div>

        {levels?.riskImpacts?.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-gray-800">
              <thead className="bg-white">
                <tr className="border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-gray-700 font-medium">Level</th>
                  <th className="px-6 py-3 text-left text-gray-700 font-medium">Score</th>
                  <th className="px-6 py-3 text-left text-gray-700 font-medium">Description</th>
                  <th className="px-6 py-3 text-left text-gray-700 font-medium">Examples</th>
                </tr>
              </thead>

              <tbody>
                {levels.riskImpacts.map((impact, index) => {
                  const color =
                    colorsImpacts.find(c => c.value === impact.impact)?.color || '#e5e7eb';

                  return (
                    <tr key={index} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-6 py-3 text-gray-900">{impact.impact}</td>

                      <td className="px-6 py-3">
                        <span
                          className="inline-flex items-center justify-center w-6 h-6 rounded-full text-[12px] font-semibold"
                          style={{ backgroundColor: `${color}22`, color }}
                        >
                          {index + 1}
                        </span>
                      </td>

                      <td className="px-6 py-3 text-gray-700">
                        <ContentEditable
                          html={editableImpact.current[index]?.operational}
                          disabled={false}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                          }}
                          onChange={(e) => { editableImpact.current[index].operational = e.target.value; }}
                          onBlur={() => {
                            editImpactFN(
                              impact.id,
                              editableImpact.current[index].operational,
                              editableImpact.current[index].financial_low,
                              editableImpact.current[index].financial_high,
                              editableImpact.current[index].reputational,
                              editableImpact.current[index].strategic
                            );
                          }}
                          tagName="span"
                        />
                      </td>

                      <td className="px-6 py-3 text-gray-700">
                        {impact.impact === 'Catastrophe' ? (
                          <>
                            <span>{'>  $'}</span>
                            <ContentEditable
                              html={String(editableImpact.current[index]?.financial_low || '')}
                              disabled={false}
                              onChange={(e) => {
                                editableImpact.current[index].financial_low = handleInputChange(e.target.value);
                              }}
                              onBlur={() => {
                                editImpactFN(
                                  impact.id,
                                  editableImpact.current[index].operational,
                                  editableImpact.current[index].financial_low,
                                  editableImpact.current[index].financial_high,
                                  editableImpact.current[index].reputational,
                                  editableImpact.current[index].strategic
                                );
                              }}
                              tagName="span"
                            />
                          </>
                        ) : impact.impact === 'Insignificant' ? (
                          <>
                            <span>{'<  $'}</span>
                            <ContentEditable
                              html={String(editableImpact.current[index]?.financial_high || '')}
                              disabled={false}
                              onChange={(e) => {
                                editableImpact.current[index].financial_high = handleInputChange(e.target.value);
                              }}
                              onBlur={() => {
                                editImpactFN(
                                  impact.id,
                                  editableImpact.current[index].operational,
                                  editableImpact.current[index].financial_low,
                                  editableImpact.current[index].financial_high,
                                  editableImpact.current[index].reputational,
                                  editableImpact.current[index].strategic
                                );
                              }}
                              tagName="span"
                            />
                          </>
                        ) : (
                          <>
                            <span>$</span>
                            <ContentEditable
                              html={String(editableImpact.current[index]?.financial_low || '')}
                              disabled={false}
                              onChange={(e) => {
                                editableImpact.current[index].financial_low = handleInputChange(e.target.value);
                              }}
                              onBlur={() => {
                                editImpactFN(
                                  impact.id,
                                  editableImpact.current[index].operational,
                                  editableImpact.current[index].financial_low,
                                  editableImpact.current[index].financial_high,
                                  editableImpact.current[index].reputational,
                                  editableImpact.current[index].strategic
                                );
                              }}
                              tagName="span"
                            />
                            <span className="px-1.5">-</span>
                            <span>$</span>
                            <ContentEditable
                              html={String(editableImpact.current[index]?.financial_high || '')}
                              disabled={false}
                              onChange={(e) => {
                                editableImpact.current[index].financial_high = handleInputChange(e.target.value);
                              }}
                              onBlur={() => {
                                editImpactFN(
                                  impact.id,
                                  editableImpact.current[index].operational,
                                  editableImpact.current[index].financial_low,
                                  editableImpact.current[index].financial_high,
                                  editableImpact.current[index].reputational,
                                  editableImpact.current[index].strategic
                                );
                              }}
                              tagName="span"
                            />
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="px-6 py-3 text-sm text-gray-500 border-t border-gray-100">
              {levels.riskImpacts.length} impact levels defined
            </div>
          </div>
        )}
      </div>
    </div>
  </>
) : (
  <Redirect to="/login" />
);


}

const mapStateToProps = (state) => ({
    token: state.auth.token,
    levels: state.risk.levels,
});

const mapDispatchToProps = (dispatch) => ({
    getLevelsFN: () => dispatch(getLevels()),
    editImpactFN: (impact_id, operational, financial_low, financial_high, reputational, strategic) =>
        dispatch(editImpact(impact_id, operational, financial_low, financial_high, reputational, strategic)),
    editProbabilityFN: (probability_id, controls, frequency) =>
        dispatch(editProbability(probability_id, controls, frequency)),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(RiskLevels);



